/**
 * Multilang Text
 *
 * Utilities for switching and updating multilingual text fragments in the UI.
 */

import { Roles } from "../constants/roles";
import { FormBase } from "./form-base-form";

type SupportedLanguage = "th" | "en" | "ja";

interface InitMultiLangTextOptions {
    root?: Document | Element;
    onAfterUpdate?: (() => void) | null;
}

const ALLOWED_LANGS: SupportedLanguage[] = ["th", "en", "ja"];

type LanguageInput = string | null | undefined;

import { MultiLangTextListener } from "./multilang-text-listener";

let isBound = false;

class MultiLangTextFormBase extends FormBase {
    public getSource(root: ParentNode, lang: SupportedLanguage): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MULTILANG_SOURCE}"][data-lang="${lang}"]`, HTMLElement);
    }

    public getAllSources(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.MULTILANG_SOURCE}"]`, HTMLElement);
    }

    public getTarget(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MULTILANG_TARGET}"]`, HTMLElement);
    }

    public getContainers(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.MULTILANG_TEXT}"]`, HTMLElement);
    }
}

const multiLangTextFormBase = new MultiLangTextFormBase();

function normalizeLanguage(lang: LanguageInput): SupportedLanguage {
    if (typeof lang === "string" && ALLOWED_LANGS.includes(lang as SupportedLanguage)) {
        return lang as SupportedLanguage;
    }
    return "th";
}

function currentLanguage(): SupportedLanguage {
    const fromStorage = localStorage.getItem("language");
    const fromDoc = document.documentElement.lang;
    return normalizeLanguage(fromStorage || fromDoc || "th");
}

function getSourceText(container: Element, lang: SupportedLanguage): string {
    const element = multiLangTextFormBase.getSource(container, lang);
    if (!(element instanceof HTMLElement)) return "";
    return (element.textContent || "").trim();
}

function pickText(container: Element, lang: SupportedLanguage): string {
    const preferred = getSourceText(container, lang);
    if (preferred) return preferred;

    const en = getSourceText(container, "en");
    if (en) return en;
    const th = getSourceText(container, "th");
    if (th) return th;
    const ja = getSourceText(container, "ja");
    if (ja) return ja;

    const any = multiLangTextFormBase.getAllSources(container);
    for (const node of any) {
        if (!(node instanceof HTMLElement)) continue;
        const text = (node.textContent || "").trim();
        if (text) return text;
    }

    return "";
}

function applyToContainer(container: Element, lang: SupportedLanguage): void {
    const target = multiLangTextFormBase.getTarget(container);
    if (!(target instanceof HTMLElement)) return;

    const text = pickText(container, lang);
    target.textContent = text;
    delete target.dataset.fullText;
    delete target.dataset.shortText;
}

function updateAll(root: Document | Element = document): void {
    const lang = currentLanguage();
    const containers = multiLangTextFormBase.getContainers(root);
    containers.forEach((container) => {
        if (!(container instanceof HTMLElement)) return;
        applyToContainer(container, lang);
    });
}

export function initMultiLangText(opts: InitMultiLangTextOptions = {}): void {
    const root = opts.root || document;
    const onAfterUpdate = typeof opts.onAfterUpdate === "function" ? opts.onAfterUpdate : null;

    updateAll(root);
    if (onAfterUpdate) onAfterUpdate();

    if (isBound) return;
    isBound = true;

    MultiLangTextListener.bindLanguageChanged(() => {
        updateAll(root);
        if (onAfterUpdate) onAfterUpdate();
    });
}
